// TableCard.tsx
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  styled,
  Typography,
  useTheme,
  Divider,
} from '@mui/material';
import { ITables, KITCHEN_ITEM_STATUS, ORDER_TYPE } from '../../../all-types';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { getKitchenItemStatusInfo } from '../../../utils';
import { useAppDispatch } from '../../../redux-store/hooks';
import { updateTable } from '../../../redux-store';
import { useTableByIdLive } from '../../../realtime';

const StatusChip = styled(Chip)(({ theme }) => ({
  fontSize: '0.8rem',
  height: 30,
  width: 110,
  fontWeight: 700,
  textTransform: 'capitalize',
  borderRadius: 8,
}));

const OrderTypeBadge = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(1, 2),
  fontSize: '0.75rem',
  fontWeight: 500,
  borderTop: `1px solid ${theme.palette.divider}`,
  borderBottomLeftRadius: theme.shape.borderRadius,
  borderBottomRightRadius: theme.shape.borderRadius,
}));

type TableCardProps = {
  table: ITables;
  isSelected?: boolean;
};

function FullMenuTableCard({ table, isSelected = false }: TableCardProps) {
  const theme = useTheme();
  const dispatcher = useAppDispatch();

  useTableByIdLive(table._id, (t) => dispatcher(updateTable(t)));

  const getTypeTextColor = (type: ORDER_TYPE | string) => {
    switch (type) {
      case ORDER_TYPE.DINE_IN:
        return theme.palette.freeTable?.main || theme.palette.success.main;
      case ORDER_TYPE.TAKEAWAY:
        return '#E33201';
      default:
        return theme.palette.text.primary;
    }
  };

  const getTypeBorderColor = (type: ORDER_TYPE | string) => {
    switch (type) {
      case ORDER_TYPE.DINE_IN:
        return '#ADEED8';
      case ORDER_TYPE.TAKEAWAY:
        return '#FFE6BB';
      default:
        return theme.palette.divider;
    }
  };

  const getTypeColor = (type: ORDER_TYPE | string) => {
    switch (type) {
      case ORDER_TYPE.DINE_IN:
        return theme.palette.color8?.main || theme.palette.background.paper;
      case ORDER_TYPE.TAKEAWAY:
        return '#FFF9ED';
      default:
        return theme.palette.background.paper;
    }
  };

  const getInstructions = (inst?: string) => {
    if (!inst) return '';
    try {
      const instructions: { [key: string]: string } = JSON.parse(inst);
      return (
        Object.entries(instructions)
          ?.map(([key, value]) => `${key}: ${value}`)
          .join(', ') || ''
      );
    } catch (e) {
      console.error(e);
      return '';
    }
  };

  if (!table?.billSession?.items?.length) return null;

  const allItemsServed = table.billSession.items.every(
    (item) => item.kitchInfo?.status === KITCHEN_ITEM_STATUS.SERVED
  );
  const orderCompletedLabel = allItemsServed ? 'Completed' : 'In progress';

  return (
    <Grid item xs={12} sm={6} lg={4}>
      <Card
        elevation={4}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          cursor: 'pointer',
          backgroundColor: 'primary.light',
          '&:hover': { boxShadow: 6 },
          borderRadius: 3,
        }}
      >
        <CardContent sx={{ flexGrow: 1, pb: 1.5 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" fontWeight="bold">
              Table No #{table?.tableNo}
            </Typography>
            <StatusChip
              label={orderCompletedLabel}
              size="small"
              sx={{
                bgcolor: allItemsServed ? theme.palette.success.light : theme.palette.occupied?.main || theme.palette.warning.light,
                color: allItemsServed ? theme.palette.success.dark : theme.palette.warning.dark,
                fontSize: '0.7rem',
              }}
            />
          </Box>

          <Divider sx={{ mb: 1.5 }} />

          {/* Column headers */}
          <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Grid item xs={6}>
              <Typography variant="body2" fontWeight="bold">Item Name</Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="body2" fontWeight="bold">Qty</Typography>
            </Grid>
            <Grid item xs={3} justifyContent="flex-end" display="flex">
              <Typography variant="body2" fontWeight="bold">Status</Typography>
            </Grid>
          </Grid>

          {/* Items list (scroll-safe) */}
          <Box sx={{ maxHeight: 280, pr: 0.5, overflowY: 'auto' }}>
            {table.billSession.items.map((item, idx) => {
              const rowKey = item?.sno ||  `${table._id}-${idx}`;
              const statusInfo = getKitchenItemStatusInfo(item?.kitchInfo?.status || KITCHEN_ITEM_STATUS.IN_KITCHEN);
              return (
                <Box key={rowKey} sx={{ mb: 1 }}>
                  <Grid container spacing={1} alignItems="flex-start">
                    <Grid item xs={6}>
                      <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                        {item.itemName}
                      </Typography>

                      {!!item?.kitchInfo?.kitchenInstrcutions && (
                        <Typography
                          sx={{ display: 'block', mt: 0.5, fontSize: 10, color: 'text.secondary', fontWeight: 600 }}
                        >
                          {getInstructions(item.kitchInfo.kitchenInstrcutions)}
                        </Typography>
                      )}

                      <Chip
                        label={`${item.quantity <= 1 ? '' : item.quantity} ${item.quantityType || ''}`.trim()}
                        sx={{ mt: 0.75, fontSize: '0.6rem', height: 24, borderRadius: 1.5 }}
                        variant="outlined"
                      />
                    </Grid>

                    <Grid item xs={2} display="flex" alignItems="center" justifyContent="center">
                      <Typography variant="subtitle2">x{item.noOfItems}</Typography>
                    </Grid>

                    <Grid item xs={3} display="flex" justifyContent="flex-end">
                      <StatusChip
                        size="small"
                        variant="outlined"
                        label={statusInfo.displayText}
                        color={statusInfo.color as any}
                        sx={{ width: 'auto', px: 1.25, height: 26 }}
                      />
                    </Grid>
                  </Grid>
                  <Divider sx={{ mt: 1 }} />
                </Box>
              );
            })}
          </Box>
        </CardContent>

        {/* Footer badge */}
        <OrderTypeBadge
          sx={{
            bgcolor: getTypeColor(ORDER_TYPE.DINE_IN),
            color: getTypeTextColor(ORDER_TYPE.DINE_IN),
            borderTop: `1px dashed ${getTypeBorderColor(ORDER_TYPE.DINE_IN)}`,
          }}
        >
          <Typography variant="caption" textTransform="capitalize" fontWeight={700}>
            {ORDER_TYPE.DINE_IN.toLowerCase()}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTimeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">{'20 mins'}</Typography>
          </Box>
        </OrderTypeBadge>
      </Card>
    </Grid>
  );
}

export { FullMenuTableCard };
